import { schCompType } from '@aloft/toolkit-shared';
import { Button, Flex, RadioGroup, Typography } from '@aloft/toolkit-sui';

import { useRef } from 'react';
import { Controller, FormProvider } from 'react-hook-form';

import { selectHasArvOption } from '../../../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../../../redux/hooks.ts';
import { AddCompsFormProps, CompEntry, CompEntryType } from '../types.ts';
import { CompEntryInput } from './CompEntryInput.tsx';

let nextEntryKey = 0;

export const AddCompsForm = ({ form }: AddCompsFormProps) => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const entries = form.watch('entries');

  // Stable keys so React doesn't reuse DOM nodes when entries are removed
  const entryKeys = useRef<number[]>([nextEntryKey++]);
  while (entryKeys.current.length < entries.length) {
    entryKeys.current.push(nextEntryKey++);
  }

  const handleAddEntry = () => {
    const current = form.getValues('entries');
    entryKeys.current.push(nextEntryKey++);
    form.setValue('entries', [...current, { method: 'mls', mls: '', mlsListingID: '' }]);
  };

  const handleRemoveEntry = (index: number) => {
    const current = form.getValues('entries');
    entryKeys.current.splice(index, 1);
    form.setValue(
      'entries',
      current.filter((_, i) => i !== index),
    );
  };

  const handleMethodChange = (index: number, method: CompEntryType) => {
    const current = form.getValues('entries');
    const reset =
      method === 'mls'
        ? { method: 'mls' as const, mls: '', mlsListingID: '' }
        : {
            method: 'address' as const,
            AddressLineText: '',
            unitNumber: '',
            CityName: '',
            StateCode: '',
            PostalCode: '',
          };
    const updated = current.map((entry, i) => (i === index ? reset : entry)) as CompEntry[];
    form.setValue('entries', updated);
  };

  return (
    <Flex width="100%" height="100%" gap={3} flexDirection="column">
      <FormProvider {...form}>
        <Flex>
          <Typography variant="body1">
            Toolkit will import data from your MLS and/or county records to autofill certain fields.
            You can still edit these fields.
          </Typography>
        </Flex>
        {hasArvOption && (
          <Controller
            control={form.control}
            key="compType"
            name="compType"
            render={({ field }) => (
              <RadioGroup
                isRequired
                label="Which Valuation Type are you adding comp(s) to?"
                size="lg"
                forceHorizontal
                columnGap="2rem"
                items={[
                  { label: 'As-Is Value', value: schCompType.enum.aiv },
                  { label: 'After-Repair Value', value: schCompType.enum.arv },
                ]}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        )}
        <Flex gap={3} flexDirection="column">
          {entries.map((entry, index) => (
            <CompEntryInput
              key={entryKeys.current[index]}
              index={index}
              method={entry.method}
              onMethodChange={handleMethodChange}
              onRemove={handleRemoveEntry}
            />
          ))}
        </Flex>
        <Flex>
          <Button
            variant="inline"
            onClick={handleAddEntry}
            text="Add another Entry"
            icon="add-circle"
          />
        </Flex>
      </FormProvider>
    </Flex>
  );
};
